import React, {useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {useTranslation} from 'react-i18next';

// material-ui
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import {useTheme} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import {
    AnyQuestion,
    DropdownQuestion,
    Form,
    FormPresentationType,
    FormTemplate,
    FormTemplateCategory,
    MultipleChoiceQuestion,
    PictureChoiceQuestion
} from "crqlar-react-common/dist/models/forms/form";
import {FormService} from "crqlar-react-common/dist/services/forms/form.service";

// ==============================|| ADD FORM DIALOG ||============================== //

type CreationMode = 'EMPTY_SINGLE' | 'EMPTY_MULTI' | 'FROM_TEMPLATE';

interface AddFormDialogProps {
    open: boolean;
    onClose: () => void;
    onCreate: (name: string, type: FormPresentationType, template?: Form) => void;
    isCreating?: boolean;
}

// Helper function to clone a form template and regenerate all UUIDs
const cloneTemplateWithNewIds = (templateForm: Form): Form => {
    const clonedForm: Form = {
        ...templateForm,
        id: uuidv4(),
        questions: templateForm.questions?.map((question) => {
            const clonedQuestion: AnyQuestion = {
                ...question,
                id: uuidv4()
            };

            // Regenerate option IDs for questions that have options
            if ('options' in clonedQuestion && clonedQuestion.options) {
                const questionWithOptions = clonedQuestion as MultipleChoiceQuestion | PictureChoiceQuestion | DropdownQuestion;
                questionWithOptions.options = questionWithOptions.options?.map(option => ({
                    ...option,
                    id: uuidv4()
                }));
            }

            return clonedQuestion;
        })
    };

    return clonedForm;
};

export default function AddFormDialog({open, onClose, onCreate, isCreating = false}: AddFormDialogProps) {
    const theme = useTheme();
    const {t} = useTranslation();

    // State
    const [formName, setFormName] = useState('');
    const [selectedMode, setSelectedMode] = useState<CreationMode | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
    const [templates, setTemplates] = useState<FormTemplate[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // Fetch templates when "From Template" mode is selected
    useEffect(() => {
        if (selectedMode === 'FROM_TEMPLATE' && templates.length === 0) {
            setLoadingTemplates(true);
            FormService.listFormTemplates()
                .then((response) => {
                    if (response) {
                        setTemplates(response);
                    }
                })
                .catch((error) => {
                    console.error('Failed to fetch templates:', error);
                })
                .finally(() => {
                    setLoadingTemplates(false);
                });
        }
    }, [selectedMode, templates.length]);

    // Handlers
    const handleClose = () => {
        onClose();
        setFormName('');
        setSelectedMode(null);
        setSelectedTemplate(null);
    };

    const handleCreateForm = () => {
        if (!formName.trim() || !selectedMode) return;

        if (selectedMode === 'FROM_TEMPLATE') {
            if (!selectedTemplate) return;

            // Clone template and regenerate IDs
            const clonedForm = cloneTemplateWithNewIds(selectedTemplate.form);

            // Update the name with the user-provided name
            clonedForm.title = formName.trim();

            onCreate(formName.trim(), clonedForm.presentationType || FormPresentationType.MULTI_PAGE, clonedForm);
        } else {
            const type = selectedMode === 'EMPTY_SINGLE' ? FormPresentationType.SINGLE_PAGE : FormPresentationType.MULTI_PAGE;
            onCreate(formName.trim(), type);
        }

        handleClose();
    };

    const getCategoryLabel = (category: FormTemplateCategory): string => {
        return category.toString().replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const getCategoryColor = (category: FormTemplateCategory): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
        switch (category) {
            default:
                return 'default';
        }
    };

    const isCreateDisabled = !formName.trim() || !selectedMode || (selectedMode === 'FROM_TEMPLATE' && !selectedTemplate) || isCreating;

    return (
        <Dialog
            open={open}
            onClose={isCreating ? undefined : handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h4">{t('Create New Form')}</Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{mt: 2}}>
                    {/* Form Name Input */}
                    <TextField
                        autoFocus
                        label={t('Form Name')}
                        placeholder={t('Enter form name')}
                        fullWidth
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        variant="outlined"
                    />

                    {/* Creation Mode Selection */}
                    <Box>
                        <Typography variant="subtitle1" sx={{mb: 2}}>
                            {t('Choose Creation Method')}
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            {/* Empty Single Page Option */}
                            <Paper
                                elevation={selectedMode === 'EMPTY_SINGLE' ? 8 : 1}
                                sx={{
                                    flex: 1,
                                    p: 2.5,
                                    cursor: 'pointer',
                                    border: 2,
                                    borderColor: selectedMode === 'EMPTY_SINGLE'
                                        ? theme.palette.primary.main
                                        : 'transparent',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.light,
                                        elevation: 4
                                    }
                                }}
                                onClick={() => {
                                    setSelectedMode('EMPTY_SINGLE');
                                    setSelectedTemplate(null);
                                }}
                            >
                                <Stack alignItems="center" spacing={1.5}>
                                    <DescriptionOutlinedIcon
                                        sx={{
                                            fontSize: 40,
                                            color: selectedMode === 'EMPTY_SINGLE'
                                                ? theme.palette.primary.main
                                                : theme.palette.text.secondary
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        textAlign="center"
                                        color={selectedMode === 'EMPTY_SINGLE'
                                            ? 'primary'
                                            : 'text.primary'}
                                    >
                                        {t('Empty Single Page')}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        textAlign="center"
                                        color="text.secondary"
                                    >
                                        {t('All questions on one page')}
                                    </Typography>
                                </Stack>
                            </Paper>

                            {/* Empty Multi Page Option */}
                            <Paper
                                elevation={selectedMode === 'EMPTY_MULTI' ? 8 : 1}
                                sx={{
                                    flex: 1,
                                    p: 2.5,
                                    cursor: 'pointer',
                                    border: 2,
                                    borderColor: selectedMode === 'EMPTY_MULTI'
                                        ? theme.palette.primary.main
                                        : 'transparent',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.light,
                                        elevation: 4
                                    }
                                }}
                                onClick={() => {
                                    setSelectedMode('EMPTY_MULTI');
                                    setSelectedTemplate(null);
                                }}
                            >
                                <Stack alignItems="center" spacing={1.5}>
                                    <LayersOutlinedIcon
                                        sx={{
                                            fontSize: 40,
                                            color: selectedMode === 'EMPTY_MULTI'
                                                ? theme.palette.primary.main
                                                : theme.palette.text.secondary
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        textAlign="center"
                                        color={selectedMode === 'EMPTY_MULTI'
                                            ? 'primary'
                                            : 'text.primary'}
                                    >
                                        {t('Empty Multi-Page')}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        textAlign="center"
                                        color="text.secondary"
                                    >
                                        {t('One question per page')}
                                    </Typography>
                                </Stack>
                            </Paper>

                            {/* From Template Option */}
                            <Paper
                                elevation={selectedMode === 'FROM_TEMPLATE' ? 8 : 1}
                                sx={{
                                    flex: 1,
                                    p: 2.5,
                                    cursor: 'pointer',
                                    border: 2,
                                    borderColor: selectedMode === 'FROM_TEMPLATE'
                                        ? theme.palette.primary.main
                                        : 'transparent',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: theme.palette.primary.light,
                                        elevation: 4
                                    }
                                }}
                                onClick={() => setSelectedMode('FROM_TEMPLATE')}
                            >
                                <Stack alignItems="center" spacing={1.5}>
                                    <InsertDriveFileOutlinedIcon
                                        sx={{
                                            fontSize: 40,
                                            color: selectedMode === 'FROM_TEMPLATE'
                                                ? theme.palette.primary.main
                                                : theme.palette.text.secondary
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        textAlign="center"
                                        color={selectedMode === 'FROM_TEMPLATE'
                                            ? 'primary'
                                            : 'text.primary'}
                                    >
                                        {t('From Template')}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        textAlign="center"
                                        color="text.secondary"
                                    >
                                        {t('Start with a template')}
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Box>

                    {/* Template Selection (shown when FROM_TEMPLATE is selected) */}
                    {selectedMode === 'FROM_TEMPLATE' && (
                        <Box>
                            <Typography variant="subtitle1" sx={{mb: 2}}>
                                {t('Select Template')}
                            </Typography>
                            {loadingTemplates ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                                    <CircularProgress/>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        // maxHeight: 400,
                                        // overflowY: 'auto',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 1
                                    }}
                                >
                                    <Stack spacing={1}>
                                        {templates.map((template) => (
                                            <Paper
                                                key={template.id}
                                                elevation={selectedTemplate?.id === template.id ? 4 : 1}
                                                sx={{
                                                    p: 2,
                                                    cursor: 'pointer',
                                                    border: 2,
                                                    borderColor: selectedTemplate?.id === template.id
                                                        ? theme.palette.primary.main
                                                        : 'transparent',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        borderColor: selectedTemplate?.id === template.id
                                                            ? theme.palette.primary.main
                                                            : theme.palette.primary.light,
                                                        elevation: 3
                                                    }
                                                }}
                                                onClick={() => {
                                                    setSelectedTemplate(template)
                                                    if (!formName || templates.find(t => t.name == formName)) {
                                                        setFormName(template.name)
                                                    }
                                                }}
                                            >
                                                <Stack direction="row" alignItems="flex-start" spacing={2}>
                                                    <Box sx={{flex: 1}}>
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {template.name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary"
                                                                    sx={{mt: 0.5}}>
                                                            {template.description}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={getCategoryLabel(template.category)}
                                                        color={getCategoryColor(template.category)}
                                                        size="small"
                                                    />
                                                </Stack>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 3}}>
                <Button onClick={handleClose} color="inherit" disabled={isCreating}>
                    {t('Cancel')}
                </Button>
                <Button
                    onClick={handleCreateForm}
                    variant="contained"
                    disabled={isCreateDisabled}
                    startIcon={isCreating ? <CircularProgress size={16}/> : null}
                >
                    {isCreating ? t('Creating...') : t('Create Form')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
